import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Heart, 
  Utensils, 
  Globe, 
  Brain, 
  Target, 
  Clock,
  MapPin,
  Shield,
  Sparkles,
  CheckCircle,
  Users,
  Database
} from 'lucide-react';
import { UserPersonalizationProfile } from '../../types/users';
import { 
  LocationStep, 
  HealthGoalsStep, 
  EatingHabitsStep, 
  FoodPreferencesStep, 
  AIPersonalityStep 
} from './OnboardingSteps';

interface EnhancedOnboardingProps {
  onComplete: (profile: Partial<UserPersonalizationProfile>) => void;
  isVisible: boolean;
  onSkip: () => void;
}

type OnboardingStep = 
  | 'welcome'
  | 'privacy'
  | 'features'
  | 'location'
  | 'health-goals'
  | 'eating-habits'
  | 'food-preferences'
  | 'ai-personality'
  | 'complete';

const EnhancedOnboarding: React.FC<EnhancedOnboardingProps> = ({ onComplete, isVisible, onSkip }) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [profile, setProfile] = useState<Partial<UserPersonalizationProfile>>({
    preferredLanguage: 'en',
    country: '',
    healthGoals: [],
    eatingHabits: {
      mealsPerDay: 3,
      snacking: 'occasionally',
      cookingFrequency: 'sometimes',
      diningOut: 'weekly'
    },
    foodPreferences: {
      favoriteCategories: [],
      favoriteCuisines: [],
      favoriteSnacks: [],
      dislikes: [],
      allergies: []
    },
    dietaryRestrictions: [],
    aiPreferences: {
      personalityStyle: 'encouraging',
      responseLength: 'brief',
      reminderFrequency: 'medium',
      challengeLevel: 'moderate'
    }
  });

  if (!isVisible) return null;

  const nextStep = () => {
    const steps: OnboardingStep[] = [
      'welcome', 'privacy', 'features', 'location', 'health-goals', 
      'eating-habits', 'food-preferences', 'ai-personality', 'complete'
    ];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    const steps: OnboardingStep[] = [
      'welcome', 'privacy', 'features', 'location', 'health-goals', 
      'eating-habits', 'food-preferences', 'ai-personality', 'complete'
    ];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleComplete = () => {
    onComplete({
      ...profile,
      onboardingStatus: {
        completed: true,
        currentStep: 9,
        completedSteps: ['welcome', 'privacy', 'features', 'location', 'health-goals', 'eating-habits', 'food-preferences', 'ai-personality'],
        skippedSteps: [],
        completedAt: new Date().toISOString()
      }
    });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center mx-auto">
              <Heart className="h-10 w-10 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">Welcome to ZippyHealth! 🎉</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                We're excited to help you on your health journey! To give you the best personalized experience, 
                we'd love to learn a bit about you. This takes just 2-3 minutes and makes ZippyHealth incredibly 
                powerful for your specific needs.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-800">
                  <Shield className="h-5 w-5" />
                  <span className="font-medium">Your privacy is protected</span>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  All your information stays on your device. No accounts, no servers, completely private.
                </p>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Your Data, Your Control</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Local Storage Only</p>
                  <p className="text-green-700 text-sm">Everything is stored on your device, not our servers</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">No Account Required</p>
                  <p className="text-green-700 text-sm">Start using immediately, no email or registration</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Export Anytime</p>
                  <p className="text-green-700 text-sm">Your data is yours - export it whenever you want</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">What Makes ZippyHealth Special</h3>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center space-x-3 p-3 bg-white border border-slate-200 rounded-lg">
                <Brain className="h-6 w-6 text-purple-600" />
                <div>
                  <p className="font-medium text-slate-800">AI-Powered Insights</p>
                  <p className="text-slate-600 text-sm">Get personalized health recommendations</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-white border border-slate-200 rounded-lg">
                <Utensils className="h-6 w-6 text-orange-600" />
                <div>
                  <p className="font-medium text-slate-800">Smart Food Tracking</p>
                  <p className="text-slate-600 text-sm">Track meals with regional food suggestions</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-white border border-slate-200 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-medium text-slate-800">Growing Community</p>
                  <p className="text-slate-600 text-sm">Connect with health experts and peers</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                <strong>Always improving:</strong> We're constantly adding new features and services based on user feedback!
              </p>
            </div>
          </div>
        );

      case 'location':
        return (
          <LocationStep 
            profile={profile} 
            updateProfile={(updates) => setProfile(prev => ({ ...prev, ...updates }))} 
          />
        );

      case 'health-goals':
        return (
          <HealthGoalsStep 
            profile={profile} 
            updateProfile={(updates) => setProfile(prev => ({ ...prev, ...updates }))} 
          />
        );

      case 'eating-habits':
        return (
          <EatingHabitsStep 
            profile={profile} 
            updateProfile={(updates) => setProfile(prev => ({ ...prev, ...updates }))} 
          />
        );

      case 'food-preferences':
        return (
          <FoodPreferencesStep 
            profile={profile} 
            updateProfile={(updates) => setProfile(prev => ({ ...prev, ...updates }))} 
          />
        );

      case 'ai-personality':
        return (
          <AIPersonalityStep 
            profile={profile} 
            updateProfile={(updates) => setProfile(prev => ({ ...prev, ...updates }))} 
          />
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">All Set! 🎉</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Perfect! We've personalized ZippyHealth just for you. Here's what you can expect:
              </p>
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <Brain className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    AI recommendations tailored to your preferences
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                  <Utensils className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-800">
                    Food suggestions based on your location and tastes
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                  <Target className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-purple-800">
                    Health insights aligned with your goals
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Step content for {currentStep}</div>;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'welcome': return 'Welcome';
      case 'privacy': return 'Privacy First';
      case 'features': return 'Features Overview';
      case 'location': return 'Your Location';
      case 'health-goals': return 'Health Goals';
      case 'eating-habits': return 'Eating Habits';
      case 'food-preferences': return 'Food Preferences';
      case 'ai-personality': return 'AI Assistant';
      case 'complete': return 'Complete';
      default: return '';
    }
  };

  const getProgressPercentage = () => {
    const steps = ['welcome', 'privacy', 'features', 'location', 'health-goals', 'eating-habits', 'food-preferences', 'ai-personality', 'complete'];
    return ((steps.indexOf(currentStep) + 1) / steps.length) * 100;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800">ZippyHealth Setup</h2>
              <p className="text-sm text-slate-600">{getStepTitle()}</p>
            </div>
            <button onClick={onSkip} className="text-slate-400 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-red-500 to-red-700 h-2 rounded-full transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">Step {Math.floor(getProgressPercentage() / 11.11)} of 9</p>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button 
              onClick={prevStep}
              disabled={currentStep === 'welcome'}
              className="flex items-center space-x-1 text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Back</span>
            </button>

            <button onClick={onSkip} className="text-sm text-slate-500 hover:text-slate-700">
              Skip for now
            </button>

            <button 
              onClick={nextStep}
              className="flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg"
            >
              <span>{currentStep === 'complete' ? 'Get Started' : 'Continue'}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedOnboarding; 
